import { useEffect, useState } from 'react';

// Keys must stay in sync with QAMate-Pro/src/constants/reportTemplates.js
export type ReportTemplateKey = 'qa-mate' | 'migros' | 'factory';

export const REPORT_TEMPLATE_KEYS: ReportTemplateKey[] = ['qa-mate', 'migros', 'factory'];

// Legacy factories (and any fetch failure) fall back to these two.
export const DEFAULT_REPORT_TEMPLATES: ReportTemplateKey[] = ['qa-mate', 'migros'];

// Display metadata — used by the cockpit "Reports Template" card.
export const TEMPLATE_META: Record<
  ReportTemplateKey,
  { name: string; icon: string; iconBg: string; iconColor: string; note: string; finalOnly?: boolean }
> = {
  'qa-mate': {
    name: 'QA Mate Template',
    icon: 'verified',
    iconBg: 'bg-blue-100',
    iconColor: 'text-[#136bce]',
    note: 'Default QA Mate branded layout — available for every report type.',
  },
  migros: {
    name: 'Migros Template',
    icon: 'inventory_2',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    note: 'Migros Asia client layout — available for every report type.',
  },
  factory: {
    name: 'Factory Template (KKP Fine Linen)',
    icon: 'factory',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-700',
    note: 'KKP Fine Linen "New Format 2026" — Final Inspection reports only.',
    finalOnly: true,
  },
};

// ── Hook: the templates the current user's organization may use ──────────────
// Fetched once per page load and memoised at module scope so all five report
// pages share one request. Fails open to DEFAULT_REPORT_TEMPLATES so a network
// hiccup never blocks report generation.
let cache: ReportTemplateKey[] | null = null;
let inflight: Promise<ReportTemplateKey[]> | null = null;

const fetchAllowed = (): Promise<ReportTemplateKey[]> => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
  const token = localStorage.getItem('token');
  inflight = fetch(`${baseUrl}/api/v1/report-templates`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => {
      const list = body?.data?.templates;
      const cleaned = Array.isArray(list)
        ? REPORT_TEMPLATE_KEYS.filter((k) => list.includes(k))
        : [];
      cache = cleaned.length > 0 ? cleaned : [...DEFAULT_REPORT_TEMPLATES];
      return cache;
    })
    .catch(() => {
      cache = [...DEFAULT_REPORT_TEMPLATES];
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

export const useAllowedReportTemplates = (): ReportTemplateKey[] => {
  const [allowed, setAllowed] = useState<ReportTemplateKey[]>(
    cache ?? DEFAULT_REPORT_TEMPLATES,
  );
  useEffect(() => {
    let alive = true;
    fetchAllowed().then((list) => {
      if (alive) setAllowed(list);
    });
    return () => {
      alive = false;
    };
  }, []);
  return allowed;
};
